import { getPlayerProgression } from '@/features/progression/logic';
import type { GuildState } from '@/types/app-state';
import type { AttendanceRecord, PlayerAttendanceStat, RoleCategory } from '@/types/domain';
import { getProfileInGameName } from '@/utils/profile';

interface PlayerAttendanceStatsIndex {
  stats: PlayerAttendanceStat[];
  statsByProfileId: Map<string, PlayerAttendanceStat>;
  attendanceByProfileId: Map<string, AttendanceRecord[]>;
}

const attendanceStatsCache = new WeakMap<GuildState, PlayerAttendanceStatsIndex>();

function sortUsage<T extends { usageCount: number }>(items: T[]): T[] {
  return [...items].sort((left, right) => right.usageCount - left.usageCount);
}

function getSafeText(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function getSafeTimestamp(value: unknown): number {
  if (typeof value !== 'string' || !value.trim()) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
}

function getAttendanceStatsIndex(state: GuildState): PlayerAttendanceStatsIndex {
  const cached = attendanceStatsCache.get(state);

  if (cached) {
    return cached;
  }

  const eventsById = new Map(state.events.map((event) => [event.id, event]));
  const signupStatsByProfileId = new Map(state.profileSignupStats.map((entry) => [entry.profileId, entry]));
  const signupCountsByProfileId = state.signups.reduce<Map<string, number>>((counts, signup) => {
    counts.set(signup.profileId, (counts.get(signup.profileId) ?? 0) + 1);
    return counts;
  }, new Map());
  const attendanceByProfileId = state.attendanceRecords.reduce<Map<string, AttendanceRecord[]>>((records, attendance) => {
    const current = records.get(attendance.profileId);

    if (current) {
      current.push(attendance);
    } else {
      records.set(attendance.profileId, [attendance]);
    }

    return records;
  }, new Map());

  const stats = state.profiles
    .filter((profile) => profile.approvalStatus === 'approved')
    .map((profile) => {
      const attendance = attendanceByProfileId.get(profile.id) ?? [];
      const signupStats = signupStatsByProfileId.get(profile.id);
      const progression = getPlayerProgression(profile, attendance.length);
      const weaponUsageMap = new Map<string, number>();
      const roleUsageMap = new Map<RoleCategory, number>();
      const displayName = getSafeText(profile.displayName, getProfileInGameName(profile));
      let lastAttendedAt: string | undefined;
      let lastAttendedMassTitle: string | undefined;
      let lastAttendanceTime = Number.NEGATIVE_INFINITY;

      for (const record of attendance) {
        const weaponName = getSafeText(record.weaponNameSnapshot, 'Unknown Weapon');
        const roleCategory = getSafeText(record.roleCategorySnapshot, 'support') as RoleCategory;
        weaponUsageMap.set(weaponName, (weaponUsageMap.get(weaponName) ?? 0) + 1);
        roleUsageMap.set(roleCategory, (roleUsageMap.get(roleCategory) ?? 0) + 1);

        const event = eventsById.get(record.eventId);
        const nextAttendedAt = event?.startsAt ?? record.attendanceLockedAt;
        const nextAttendanceTime = getSafeTimestamp(nextAttendedAt);

        if (nextAttendanceTime > lastAttendanceTime) {
          lastAttendanceTime = nextAttendanceTime;
          lastAttendedAt = Number.isFinite(nextAttendanceTime) ? nextAttendedAt : undefined;
          lastAttendedMassTitle = getSafeText(event?.title, '');
        }
      }

      return {
        profileId: profile.id,
        inGameName: getProfileInGameName(profile),
        displayName,
        roles: profile.roles,
        badges: profile.badges,
        totalSignupRecords: signupStats?.totalSignupRecords ?? signupCountsByProfileId.get(profile.id) ?? 0,
        totalCountedMasses: attendance.length,
        progressionLevel: progression.level,
        progressionTier: progression.tier,
        lastAttendedAt,
        lastAttendedMassTitle,
        weaponUsage: sortUsage(
          Array.from(weaponUsageMap.entries()).map(([weaponName, usageCount]) => ({
            weaponName,
            usageCount,
          })),
        ),
        roleUsage: sortUsage(
          Array.from(roleUsageMap.entries()).map(([roleCategory, usageCount]) => ({
            roleCategory,
            usageCount,
          })),
        ),
      } satisfies PlayerAttendanceStat;
    })
    .sort((left, right) => {
      if (right.totalCountedMasses !== left.totalCountedMasses) {
        return right.totalCountedMasses - left.totalCountedMasses;
      }

      if (right.totalSignupRecords !== left.totalSignupRecords) {
        return right.totalSignupRecords - left.totalSignupRecords;
      }

      return left.inGameName.localeCompare(right.inGameName);
    });

  const statsByProfileId = new Map(stats.map((entry) => [entry.profileId, entry]));
  const nextIndex = {
    stats,
    statsByProfileId,
    attendanceByProfileId,
  } satisfies PlayerAttendanceStatsIndex;

  attendanceStatsCache.set(state, nextIndex);
  return nextIndex;
}

export function getPlayerAttendanceStats(state: GuildState): PlayerAttendanceStat[] {
  return getAttendanceStatsIndex(state).stats;
}

export function getPlayerAttendanceStat(state: GuildState, profileId: string): PlayerAttendanceStat | null {
  return getAttendanceStatsIndex(state).statsByProfileId.get(profileId) ?? null;
}

export function getPlayerAttendanceHistory(state: GuildState, profileId: string): AttendanceRecord[] {
  return getAttendanceStatsIndex(state).attendanceByProfileId.get(profileId) ?? [];
}
